/**
 * Rate preview rail — `POST /admin/data/rtl-pricing/preview`.
 *
 * Runs the same `compute_rtl_rate` the sizer uses against the SAVED config, so
 * it answers "what does this configuration actually price?" — it does NOT
 * reflect unsaved edits in the sections beside it. That distinction is called
 * out in the rail itself rather than left implicit, because the panel looks
 * live and reading it as live would be wrong.
 *
 * Response components (rates are decimal fractions; adjustments are PERCENT
 * verbatim — `rateUnits: "percent"`, bps retired 2026-08-19):
 *   baseRate12mo · termAdjPct · classAdjPct · loanAmountUptickPct (+uptickTier)
 *   leverageAdjPct (+leverageBand) · requiredBaseRate · accountSpread ·
 *   requiredCoupon (HEADLINE = base + spread, what the borrower pays) · buyRate
 *   (= required base, BELOW the coupon — owner 2026-10-08, "this is backwards")
 *   coupon / stripRate when a coupon is supplied (strip = coupon − buyRate)
 * An unpriceable product comes back as `{error}` rather than an HTTP failure.
 */
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminRtlPricingApi } from "../../../api/adminDataApi";
import { rtlBuyRate, rtlRequiredCoupon } from "../../sizersShared/rtlRateDisplay";
import { PricingField, T } from "./pricingUi";

const CLASSES = ["A+", "A", "B", "C"];
const TERMS = [12, 18, 24, 36];

/** Each half of a two-up field row. */
const halfSx = { flex: 1, minWidth: 0 };

/**
 * A percent typed into the rail → the decimal fraction the endpoint wants
 * (75 → 0.75, 8.25 → 0.0825). Empty stays null so an optional field reads as
 * "not supplied" rather than zero.
 */
const fractionOf = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n / 100 : null;
};

const pctFromFraction = (v) =>
  v == null ? "—" : `${(Number(v) * 100).toFixed(3)}%`;
// Adjustments come back as a PERCENT verbatim (bps retired from RTL pricing
// 2026-08-19) — render as-is, no scaling. The build-up stacks in the same unit
// as the rates it adds to. THREE decimals, like the sizer's fmtAdjPct: at two,
// a stored 0.125 renders "+0.13%" and the table shows a number the engine is
// not using — exactly what hid the bps bug (rtl-pricing-units-contract.md).
const adjPct = (v) =>
  v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(3)}%`;

/** One line of the additive breakdown. */
const LedgerRow = ({ label, sub, value, zero }) => (
  <Flx
    ac
    jb
    g={1}
    sx={{
      py: 0.875,
      borderBottom: `1px dashed ${T.line}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Flx column g={0.125} sx={{ minWidth: 0 }}>
      <Txt sx={{ fontSize: 12, color: T.muted }}>{label}</Txt>
      {sub ? <Txt sx={{ fontSize: 10.5, color: T.faint }}>{sub}</Txt> : null}
    </Flx>
    <Txt
      sx={{
        fontFamily: T.mono,
        fontSize: 12.5,
        fontWeight: 500,
        color: zero ? T.faint : T.ink,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Txt>
  </Flx>
);

LedgerRow.propTypes = {
  label: PropTypes.string,
  sub: PropTypes.string,
  value: PropTypes.node,
  zero: PropTypes.bool,
};

export default function RtlRatePreview({ products }) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState({
    product: "",
    termMonths: 12,
    borrowerClass: "A",
    loanAmount: 1000000,
    // LTV, spread and coupon are all held HERE as percents (the desk's unit) and
    // divided by 100 on the way to the endpoint, which speaks decimal fractions.
    ltv: 75,
    accountSpread: 1,
    coupon: "",
    lqMode: false,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const run = useCallback(() => {
    setBusy(true);
    adminRtlPricingApi
      .preview({
        product: form.product || products?.[0],
        termMonths: Number(form.termMonths) || 12,
        borrowerClass: form.borrowerClass,
        loanAmount: Number(form.loanAmount) || 0,
        ltv: fractionOf(form.ltv),
        accountSpread: fractionOf(form.accountSpread) ?? 0,
        coupon: fractionOf(form.coupon),
        lqMode: form.lqMode,
      })
      .then(setResult)
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.detail || "Preview failed", {
          variant: "error",
        }),
      )
      .finally(() => setBusy(false));
  }, [form, products, enqueueSnackbar]);

  // "The preview panel should re-run POST …/preview on any input change"
  // (admin-rtl-pricing-config.md). Debounced so typing into the amount/LTV
  // fields doesn't fire a request per keystroke; the button stays as an
  // explicit retry after a failed run.
  useEffect(() => {
    if (!(form.product || products?.[0])) return undefined;
    const id = setTimeout(run, 350);
    return () => clearTimeout(id);
  }, [form, products, run]);

  const fieldSx = { "& .MuiInputBase-input": { fontSize: 12.5 } };

  return (
    <Box>
      <Box
        sx={{
          bgcolor: T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: "10px",
          overflow: "hidden",
          boxShadow: "0 4px 16px -2px rgba(22,33,46,.08)",
        }}
      >
        <Flx
          ac
          jb
          g={1}
          sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${T.line}` }}
        >
          <Txt sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
            Rate preview
          </Txt>
          <Box
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".06em",
              textTransform: "uppercase",
              color: T.muted,
              bgcolor: T.surface2,
              border: `1px solid ${T.line}`,
              borderRadius: "20px",
              px: 1,
              py: 0.25,
            }}
          >
            Saved config
          </Box>
        </Flx>

        {/* Roomier than the old floating-label stack — a label above its control
            needs the extra air to read as belonging to the field below it. */}
        <Flx
          column
          g={1.75}
          sx={{ p: 2, borderBottom: `1px solid ${T.line}` }}
        >
          <PricingField label="Product">
            <TextField
              select
              size="small"
              value={form.product || products?.[0] || ""}
              onChange={(e) => set("product", e.target.value)}
              fullWidth
              sx={fieldSx}
            >
              {(products || []).map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </PricingField>

          <Flx g={1.25} sx={{ alignItems: "flex-end" }}>
            <PricingField label="Term (mo)" sx={halfSx}>
              <TextField
                select
                size="small"
                value={form.termMonths}
                onChange={(e) => set("termMonths", e.target.value)}
                fullWidth
                sx={fieldSx}
              >
                {TERMS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </PricingField>
            <PricingField label="Class" sx={halfSx}>
              <TextField
                select
                size="small"
                value={form.borrowerClass}
                onChange={(e) => set("borrowerClass", e.target.value)}
                fullWidth
                sx={fieldSx}
              >
                {CLASSES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </PricingField>
          </Flx>

          <Flx g={1.25} sx={{ alignItems: "flex-end" }}>
            <PricingField label="Loan amount" sx={halfSx}>
              <TextField
                size="small"
                value={form.loanAmount}
                onChange={(e) => set("loanAmount", e.target.value)}
                fullWidth
                sx={fieldSx}
              />
            </PricingField>
            <PricingField label="LTV (%)" sx={halfSx}>
              <TextField
                size="small"
                value={form.ltv}
                onChange={(e) => set("ltv", e.target.value)}
                fullWidth
                sx={fieldSx}
              />
            </PricingField>
          </Flx>

          <Flx g={1.25} sx={{ alignItems: "flex-end" }}>
            <PricingField label="Account spread (%)" sx={halfSx}>
              <TextField
                size="small"
                value={form.accountSpread}
                onChange={(e) => set("accountSpread", e.target.value)}
                fullWidth
                sx={fieldSx}
              />
            </PricingField>
            <PricingField label="Coupon (%, opt.)" sx={halfSx}>
              <TextField
                size="small"
                value={form.coupon}
                onChange={(e) => set("coupon", e.target.value)}
                fullWidth
                sx={fieldSx}
              />
            </PricingField>
          </Flx>

          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Checkbox
                size="small"
                checked={form.lqMode}
                onChange={(e) => set("lqMode", e.target.checked)}
                sx={{ p: 0.5, mr: 0.75 }}
              />
            }
            label={
              <Txt sx={{ fontSize: 12, color: T.ink2 }}>
                LQ mode (coupon = buy rate)
              </Txt>
            }
          />

          <Button variant="contained" onClick={run} disabled={busy} fullWidth>
            Run preview
          </Button>
        </Flx>

        {!result ? (
          <Txt
            sx={{
              display: "block",
              fontSize: 12,
              color: T.faint,
              textAlign: "center",
              py: 3,
              px: 2,
            }}
          >
            Run a preview to see the additive breakdown.
          </Txt>
        ) : result.error ? (
          <Txt sx={{ display: "block", fontSize: 12.5, color: "#b91c1c", p: 2 }}>
            {result.error}
          </Txt>
        ) : (
          <>
            {/* The payload states its own scale — read it rather than assuming
                percent. A different marker means the adjustments below are on
                another scale and the build-up cannot be trusted. */}
            {result.rateUnits && result.rateUnits !== "percent" ? (
              <Txt
                sx={{
                  display: "block",
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  color: T.warn,
                  bgcolor: T.warnSoft,
                  borderTop: `1px solid ${T.warnLine}`,
                  borderBottom: `1px solid ${T.warnLine}`,
                  px: 2,
                  py: 1,
                }}
              >
                Rate units came back as{" "}
                <Box component="span" sx={{ fontFamily: T.mono }}>
                  {String(result.rateUnits)}
                </Box>
                , not percent — the adjustments below are on another scale.
              </Txt>
            ) : null}
            <Box sx={{ px: 2, py: 0.5 }}>
              <LedgerRow
                label="Base rate (12 mo)"
                value={pctFromFraction(result.baseRate12mo)}
              />
              <LedgerRow
                label="Term adjustment"
                value={adjPct(result.termAdjPct)}
                zero={!result.termAdjPct}
              />
              <LedgerRow
                label="Class adjustment"
                value={adjPct(result.classAdjPct)}
                zero={!result.classAdjPct}
              />
              <LedgerRow
                label="Loan-size uptick"
                sub={result.uptickTier}
                value={adjPct(result.loanAmountUptickPct)}
                zero={!result.loanAmountUptickPct}
              />
              <LedgerRow
                label="Leverage adjustment"
                sub={result.leverageBand}
                value={adjPct(result.leverageAdjPct)}
                zero={!result.leverageAdjPct}
              />
              <LedgerRow
                label="Required base rate"
                value={pctFromFraction(result.requiredBaseRate)}
              />
              <LedgerRow
                label="Account spread"
                value={pctFromFraction(result.accountSpread)}
              />
              {rtlBuyRate(result) != null ? (
                <LedgerRow
                  label="Buy rate"
                  sub="investor, = required base"
                  value={pctFromFraction(rtlBuyRate(result))}
                />
              ) : null}
              {result.coupon != null ? (
                <>
                  <LedgerRow
                    label="Coupon"
                    value={pctFromFraction(result.coupon)}
                  />
                  <LedgerRow
                    label="Strip"
                    value={pctFromFraction(result.stripRate)}
                  />
                </>
              ) : null}
            </Box>

            <Box
              sx={{
                bgcolor: T.accentSoft,
                borderTop: `1px solid ${T.line}`,
                px: 2,
                py: 1.75,
              }}
            >
              <Txt
                sx={{
                  display: "block",
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  color: T.accentInk,
                }}
              >
                Required coupon
              </Txt>
              <Txt
                sx={{
                  display: "block",
                  fontFamily: T.mono,
                  fontSize: 28,
                  fontWeight: 600,
                  letterSpacing: "-.02em",
                  lineHeight: 1.15,
                  color: T.accentInk,
                }}
              >
                {pctFromFraction(rtlRequiredCoupon(result))}
              </Txt>
              <Txt
                sx={{
                  display: "block",
                  fontFamily: T.mono,
                  fontSize: 10.5,
                  color: T.muted,
                  mt: 0.25,
                }}
              >
                required base {pctFromFraction(result.requiredBaseRate)} + spread{" "}
                {pctFromFraction(result.accountSpread)}
                {rtlBuyRate(result) != null
                  ? ` · buy rate ${pctFromFraction(rtlBuyRate(result))}`
                  : ""}
              </Txt>
            </Box>
          </>
        )}
      </Box>

      <Txt
        sx={{
          display: "block",
          fontSize: 11,
          color: T.faint,
          lineHeight: 1.55,
          px: 0.5,
          pt: 1.25,
        }}
      >
        Prices the saved configuration — save a section before previewing its
        effect. LTV, spread and coupon are entered as percents (75 = 0.75 LTV,
        1.00 = a 1.00% spread).
      </Txt>
    </Box>
  );
}

RtlRatePreview.propTypes = { products: PropTypes.array };
